import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, from, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { AngularFireMessaging } from '@angular/fire/messaging';
import { AngularFirestore } from '@angular/fire/firestore';
import { API } from '@core/services/api.base';
import {
  NotificationEntity,
  NotificationPayload,
  FCMToken,
} from 'src/app/models/notification.model';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store';
import * as UserProfileSelectors from 'src/app/store/user-profile/user-profile.selector';
import { ToastService } from '../toast/toast.service';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class NotificationService extends API implements OnDestroy {
  private notificationSubject = new BehaviorSubject<NotificationEntity[]>([]);
  private readNotificationSubject = new BehaviorSubject<NotificationEntity[]>(
    []
  );
  private originalNotifications: NotificationEntity[] = [];
  private currentUserId: string | null = null;
  private notificationSubscription: Subscription | null = null;
  private readNotificationSubscription: Subscription | null = null;

  constructor(
    protected http: HttpClient,
    private store: Store<AppState>,
    private toastService: ToastService,
    private router: Router,
    private afMessaging: AngularFireMessaging,
    private firestore: AngularFirestore
  ) {
    super(http);
    this.initializeNotifications();
  }

  public get notifications$(): Observable<NotificationEntity[]> {
    return this.notificationSubject.asObservable();
  }

  public get readNotifications$(): Observable<NotificationEntity[]> {
    return this.readNotificationSubject.asObservable();
  }

  public get unreadCount$(): Observable<number> {
    return new BehaviorSubject(
      this.notificationSubject.value.filter((n) => !n.read).length
    ).asObservable();
  }

  private async initializeNotifications(): Promise<void> {
    // Subscribe to user profile changes
    this.store
      .select(UserProfileSelectors.selectCurrentUserProfile)
      .subscribe((userProfile) => {
        if (userProfile && userProfile.id !== this.currentUserId) {
          this.currentUserId = userProfile.id;
          this.setupNotificationListener(userProfile.id);
          this.requestNotificationPermission();
        } else if (!userProfile) {
          this.currentUserId = null;
          this.clearNotifications();
        }
      });

    // Setup foreground message listener
    this.setupForegroundMessageListener();
  }

  private async requestNotificationPermission(): Promise<void> {
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        console.log('Notification permission granted');
        await this.getFCMToken();
      } else {
        console.log('Notification permission denied');
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    }
  }

  private async getFCMToken(): Promise<string | null> {
    try {
      // Prepare token request options
      const tokenOptions: any = {};

      // Add VAPID key if it's properly configured (not placeholder)
      if (
        environment.firebase?.vapidKey &&
        environment.firebase.vapidKey !== 'YOUR_VAPID_KEY_HERE'
      ) {
        tokenOptions.vapidKey = environment.firebase.vapidKey;
      }

      // Get the FCM token
      const token = await this.afMessaging.getToken.pipe(take(1)).toPromise();

      if (token && this.currentUserId) {
        console.log('FCM Token:', token);
        await this.saveFCMToken(token, this.currentUserId);
        return token;
      }
      return null;
    } catch (error) {
      console.error('Error getting FCM token:', error);

      // If service worker registration failed, try without any options
      try {
        console.log('Retrying FCM token generation with default settings...');
        const fallbackToken = await this.afMessaging.getToken
          .pipe(take(1))
          .toPromise();

        if (fallbackToken && this.currentUserId) {
          console.log('FCM Token (fallback):', fallbackToken);
          await this.saveFCMToken(fallbackToken, this.currentUserId);
          return fallbackToken;
        }
      } catch (fallbackError) {
        console.error(
          'Fallback FCM token generation also failed:',
          fallbackError
        );
      }

      return null;
    }
  }

  private async saveFCMToken(token: string, userId: string): Promise<void> {
    try {
      const fcmToken: Partial<FCMToken> = {
        token,
        userId,
        deviceType: 'web',
        createdAt: new Date(),
        lastUsed: new Date(),
      };

      await this.post<void, Partial<FCMToken>>(
        'v1/fcm-tokens',
        fcmToken
      ).toPromise();
      console.log('FCM token saved successfully');
    } catch (error) {
      console.error('Error saving FCM token:', error);
    }
  }

  private setupForegroundMessageListener(): void {
    this.afMessaging.messages.subscribe((payload: any) => {
      console.log('Foreground message received:', payload);
      this.handleForegroundMessage(payload);
    });
  }

  private handleForegroundMessage(payload: any): void {
    const { notification, data } = payload;

    if (notification) {
      // Show toast notification
      this.toastService.info(
        notification.title || 'New Notification',
        notification.body || 'You have a new notification',
        [
          {
            label: 'View',
            action: () => this.handleNotificationClick(data),
            style: 'primary',
          },
        ]
      );

      // Add to notifications list
      this.addNotificationToList({
        id: Date.now().toString(),
        type: data?.type || 'GENERAL',
        title: notification.title || 'Notification',
        message: notification.body || '',
        data: data,
        read: false,
        createdTime: new Date(),
        modifiedTime: new Date(),
        userId: this.currentUserId || '',
        imageUrl: notification.icon,
      });
    }
  }

  private handleNotificationClick(data: any): void {
    if (data?.appointmentId) {
      this.router.navigate(['/appointments', data.appointmentId]);
    } else if (data?.orderId) {
      this.router.navigate(['/orders', data.orderId]);
    } else if (data?.storeId) {
      this.router.navigate(['/businesses', data.storeId]);
    } else {
      this.router.navigate(['/notifications']);
    }
  }

  private async setupNotificationListener(userId: string): Promise<void> {
    try {
      console.log(
        '🚀 ~ NotificationService ~ setupNotificationListener ~ userId:',
        userId
      );

      // Clean up existing subscriptions
      this.cleanupSubscriptions();

      // Setup unread notifications listener
      await this.listenToNotification(userId);

      // Setup read notifications listener
      await this.listenToReadNotification(userId);
    } catch (error) {
      console.error('Error setting up notification listener:', error);
    }
  }

  private cleanupSubscriptions(): void {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
      this.notificationSubscription = null;
    }
    if (this.readNotificationSubscription) {
      this.readNotificationSubscription.unsubscribe();
      this.readNotificationSubscription = null;
    }
  }

  public async listenToNotification(userId: string): Promise<void> {
    console.log(
      '🚀 ~ NotificationService ~ listenToNotification ~ userId:',
      userId
    );

    if (this.notificationSubscription) {
      console.log('removing existing unread notification listener');
      this.notificationSubscription.unsubscribe();
    }

    console.log('adding unread notification listener');

    this.notificationSubscription = this.firestore
      .collection(`notification/${userId}/notificationData`, (ref) =>
        ref.where('read', '==', false).orderBy('createdTime', 'desc').limit(50)
      )
      .valueChanges({ idField: 'id' })
      .subscribe({
        next: (data: any[]) => {
          console.log('📱 Unread notifications received:', data);

          const result = data.map((documentData) => {
            // Handle both Firestore Timestamp objects and millisecond timestamps
            const {
              createdTime: _originalCreatedTime,
              modifiedTime: _originalModifiedTime,
              readAt: _originalReadAt,
              ...cleanData
            } = documentData;

            return {
              ...cleanData,
              // Use millisecond timestamps as Date objects (universal format)
              createdTime: documentData.createdTimeMs
                ? new Date(documentData.createdTimeMs)
                : documentData.createdTime?.toDate
                ? documentData.createdTime.toDate()
                : new Date(documentData.createdTime),
              modifiedTime: documentData.modifiedTimeMs
                ? new Date(documentData.modifiedTimeMs)
                : documentData.modifiedTime?.toDate
                ? documentData.modifiedTime.toDate()
                : new Date(documentData.modifiedTime),
              readAt: documentData.readAtMs
                ? new Date(documentData.readAtMs)
                : documentData.readAt?.toDate
                ? documentData.readAt.toDate()
                : documentData.readAt
                ? new Date(documentData.readAt)
                : undefined,
            } as NotificationEntity;
          });

          console.log('Processed unread notifications:', result);
          this.notificationSubject.next(result);
        },
        error: (error) => {
          console.error('Error listening to unread notifications:', error);
        },
      });
  }

  public async listenToReadNotification(userId: string): Promise<void> {
    console.log(
      '🚀 ~ NotificationService ~ listenToReadNotification ~ userId:',
      userId
    );

    if (this.readNotificationSubscription) {
      console.log('removing existing read notification listener');
      this.readNotificationSubscription.unsubscribe();
    }

    console.log('adding read notification listener');

    this.readNotificationSubscription = this.firestore
      .collection(`notification/${userId}/notificationData`, (ref) =>
        ref.where('read', '==', true).orderBy('createdTime', 'desc').limit(20)
      )
      .valueChanges({ idField: 'id' })
      .subscribe({
        next: (data: any[]) => {
          console.log('📱 Read notifications received:', data);

          const result = data.map((documentData) => {
            // Handle both Firestore Timestamp objects and millisecond timestamps
            const {
              createdTime: _originalCreatedTime,
              modifiedTime: _originalModifiedTime,
              readAt: _originalReadAt,
              ...cleanData
            } = documentData;

            return {
              ...cleanData,
              // Use millisecond timestamps as Date objects (universal format)
              createdTime: documentData.createdTimeMs
                ? new Date(documentData.createdTimeMs)
                : documentData.createdTime?.toDate
                ? documentData.createdTime.toDate()
                : new Date(documentData.createdTime),
              modifiedTime: documentData.modifiedTimeMs
                ? new Date(documentData.modifiedTimeMs)
                : documentData.modifiedTime?.toDate
                ? documentData.modifiedTime.toDate()
                : new Date(documentData.modifiedTime),
              readAt: documentData.readAtMs
                ? new Date(documentData.readAtMs)
                : documentData.readAt?.toDate
                ? documentData.readAt.toDate()
                : documentData.readAt
                ? new Date(documentData.readAt)
                : undefined,
            } as NotificationEntity;
          });

          console.log('Processed read notifications:', result);
          this.readNotificationSubject.next(result);
        },
        error: (error) => {
          console.error('Error listening to read notifications:', error);
        },
      });
  }

  private addNotificationToList(notification: NotificationEntity): void {
    const currentNotifications = this.notificationSubject.value;
    this.notificationSubject.next([notification, ...currentNotifications]);
  }

  private clearNotifications(): void {
    this.cleanupSubscriptions();
    this.notificationSubject.next([]);
    this.readNotificationSubject.next([]);
  }

  public async markAsRead(notificationId: string): Promise<void> {
    if (!this.currentUserId) return;

    // Store original state for rollback
    this.storeOriginalState();

    // Optimistic update
    this.optimisticMarkAsRead(notificationId);

    try {
      await this.firestore
        .doc(
          `notification/${this.currentUserId}/notificationData/${notificationId}`
        )
        .update({
          read: true,
          readAt: new Date(),
          readAtMs: Date.now(),
          modifiedTime: new Date(),
          modifiedTimeMs: Date.now(),
        });

      console.log('Notification marked as read:', notificationId);
    } catch (error) {
      this.rollbackToOriginalState();
      console.error('Error marking notification as read:', error);
    }
  }

  public async markAllAsRead(): Promise<void> {
    if (!this.currentUserId) return;

    // Store original state for rollback
    this.storeOriginalState();

    // Optimistic update
    this.optimisticMarkAllAsRead();

    try {
      this.post<null, { userId: string }>('v1/notification/mark-all-as-read', {
        userId: this.currentUserId,
      }).subscribe({
        next: () => {
          console.log('Notifications marked as read');
        },
        error: (error) => {
          this.rollbackToOriginalState();
          console.error('Error marking all notifications as read:', error);
        },
      });
    } catch (error) {
      this.rollbackToOriginalState();
      console.error('Error marking all notifications as read:', error);
    }
  }

  public async refreshNotifications(): Promise<void> {
    if (this.currentUserId) {
      await this.setupNotificationListener(this.currentUserId);
    }
  }

  private storeOriginalState(): void {
    this.originalNotifications = [...this.notificationSubject.value];
  }

  private rollbackToOriginalState(): void {
    console.log('Rolling back to original state due to error');
    this.notificationSubject.next([...this.originalNotifications]);
  }

  private optimisticMarkAsRead(notificationId: string): void {
    const currentNotifications = this.notificationSubject.value;
    const updatedNotifications = currentNotifications.map((notification) => {
      if (notification.id === notificationId) {
        return {
          ...notification,
          read: true,
          readAt: new Date(),
        };
      }
      return notification;
    });

    // Move read notification to read list
    const readNotification = updatedNotifications.find(
      (n) => n.id === notificationId
    );
    if (readNotification) {
      const currentReadNotifications = this.readNotificationSubject.value;
      this.readNotificationSubject.next([
        readNotification,
        ...currentReadNotifications,
      ]);
    }

    // Remove from unread list
    const unreadNotifications = updatedNotifications.filter(
      (n) => n.id !== notificationId
    );
    this.notificationSubject.next(unreadNotifications);
  }

  private optimisticMarkAllAsRead(): void {
    const currentNotifications = this.notificationSubject.value;
    const updatedNotifications = currentNotifications.map((notification) => ({
      ...notification,
      read: true,
      readAt: new Date(),
    }));

    // Move all to read list
    const currentReadNotifications = this.readNotificationSubject.value;
    this.readNotificationSubject.next([
      ...updatedNotifications,
      ...currentReadNotifications,
    ]);

    // Clear unread list
    this.notificationSubject.next([]);
  }

  public getNotificationIcon(type: string): string {
    switch (type) {
      case 'APPOINTMENT_CREATED':
      case 'APPOINTMENT_CONFIRMED':
        return 'fas fa-calendar-check';
      case 'APPOINTMENT_CANCELED':
        return 'fas fa-calendar-times';
      case 'APPOINTMENT_REMINDER':
        return 'fas fa-bell';
      case 'APPOINTMENT_CHECKED_IN':
        return 'fas fa-check-circle';
      case 'APPOINTMENT_COMPLETED':
        return 'fas fa-check-double';
      case 'ORDER_STATUS_UPDATED':
        return 'fas fa-shopping-bag';
      case 'PROMOTION_AVAILABLE':
        return 'fas fa-gift';
      default:
        return 'fas fa-info-circle';
    }
  }

  public getNotificationColor(type: string): string {
    switch (type) {
      case 'APPOINTMENT_CREATED':
      case 'APPOINTMENT_CONFIRMED':
      case 'APPOINTMENT_COMPLETED':
        return 'text-success';
      case 'APPOINTMENT_CANCELED':
        return 'text-danger';
      case 'APPOINTMENT_REMINDER':
        return 'text-warning';
      case 'APPOINTMENT_CHECKED_IN':
        return 'text-info';
      case 'ORDER_STATUS_UPDATED':
        return 'text-primary';
      case 'PROMOTION_AVAILABLE':
        return 'text-warning';
      default:
        return 'text-muted';
    }
  }

  public formatNotificationTime(date: Date): string {
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      // 24 hours
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days}d ago`;
    }
  }

  ngOnDestroy(): void {
    this.cleanupSubscriptions();
  }
}
